import type { ArapProblem, ArapSolveOptions } from './ArapCore';

export type ArapWorkerRequest =
  | { type: 'bind'; problem: ArapProblem }
  | { type: 'solve'; reqId: number; constrained: Float64Array; options: ArapSolveOptions };

type ArapWorkerResponse =
  | { type: 'bound' }
  | { type: 'solved'; reqId: number; free: Float64Array };

/**
 * Main-thread client for {@link arapWorker}. Owns one worker holding one bound `ArapCore`,
 * and turns a solve into a `Promise<Float64Array>` of free-vertex positions. Falls back
 * cleanly (returns `false`/`null`) if the worker can't be created, so the caller can run
 * the solve synchronously instead.
 */
export class ArapWorkerClient {
  private worker: Worker | null = null;
  private reqId = 0;
  private readonly pending = new Map<number, (free: Float64Array) => void>();
  private failed = false;

  public get available(): boolean {
    return !this.failed;
  }

  private ensure(): Worker | null {
    if (this.failed) return null;
    if (this.worker) return this.worker;
    try {
      const w = new Worker(new URL('./arapWorker.ts', import.meta.url), { type: 'module' });
      w.onmessage = (ev: MessageEvent<ArapWorkerResponse>) => {
        const m = ev.data;
        if (m.type === 'solved') {
          const resolve = this.pending.get(m.reqId);
          if (resolve) {
            this.pending.delete(m.reqId);
            resolve(m.free);
          }
        }
      };
      w.onerror = () => {
        // Disable the worker path permanently; the caller falls back to a sync solve.
        this.failed = true;
        this.pending.clear();
        this.worker?.terminate();
        this.worker = null;
      };
      this.worker = w;
      return w;
    } catch {
      this.failed = true;
      return null;
    }
  }

  /** (Re)binds the worker's core to `problem`. Returns false if the worker is unavailable. */
  public bind(problem: ArapProblem): boolean {
    const w = this.ensure();
    if (!w) return false;
    // Structure-clone (no transfer) so the main thread keeps the topology arrays.
    w.postMessage({ type: 'bind', problem });
    // Any in-flight solves belong to the previous binding — drop them.
    this.pending.clear();
    return true;
  }

  /**
   * Runs a solve. `constrained` is copied (the caller reuses its scratch buffer) and
   * transferred to the worker. Resolves with the free-vertex positions (`3·nFree`).
   * Returns null if the worker is unavailable.
   */
  public solve(constrained: Float64Array, options: ArapSolveOptions): Promise<Float64Array> | null {
    const w = this.ensure();
    if (!w) return null;
    const reqId = ++this.reqId;
    const buf = constrained.slice();
    return new Promise<Float64Array>((resolve) => {
      this.pending.set(reqId, resolve);
      w.postMessage({ type: 'solve', reqId, constrained: buf, options }, [buf.buffer]);
    });
  }

  public dispose(): void {
    this.worker?.terminate();
    this.worker = null;
    this.pending.clear();
  }
}
